/// System Settings API wrapper (SYS-map-provider).
/// client-config: any authenticated user (first-load) · entries: Super Admin only.
/// Reference: SYS-map-provider API Contracts §1–3, Frontend DD §5.

import { apiClient } from '@/core/api/axios-client'
import type {
  ClientConfigResponse,
  GoogleMapsKeyReport,
  SystemSettingResponse,
} from '@/types/api'

export const systemSettingsApi = {
  /** First-load client config — effective values of target web|all settings (any auth user). */
  async clientConfig(): Promise<ClientConfigResponse> {
    const res = await apiClient.get<ClientConfigResponse>('/system-settings/client-config')
    return res.data
  },

  /** List all settings (Super Admin); optional exact target filter. */
  async list(target?: string): Promise<SystemSettingResponse[]> {
    const res = await apiClient.get<SystemSettingResponse[]>('/system-settings/entries', {
      params: target ? { target } : undefined,
    })
    return res.data
  },

  /** Update one setting's value (Super Admin). value=null resets to default. */
  async update(key: string, value: string | null): Promise<SystemSettingResponse> {
    const res = await apiClient.put<SystemSettingResponse>(
      `/system-settings/entries/${key}`,
      { value },
    )
    return res.data
  },

  /**
   * Ask the backend to check a Google Maps key against Google (Super Admin).
   * The key goes in the BODY (it is a credential — query strings land in access logs) and
   * need not be saved yet. Purely advisory: the answer never gates the save call.
   */
  async validateGoogleMapsKey(key: string): Promise<GoogleMapsKeyReport> {
    const res = await apiClient.post<GoogleMapsKeyReport>(
      '/system-settings/google-maps-key/validate',
      // `origin` lets the backend ask Google whether a referrer-restricted key would work
      // on THIS domain — the admin console is served from the same origin as the map
      // screens, so the answer is the one that matters.
      { key, origin: window.location.origin },
    )
    return res.data
  },
}
